import React, { useState } from 'react'
import {Brain, Zap, TrendingUp, BarChart3, Sparkles} from 'lucide-react'
import BookingModal from './BookingModal'

const aiServices = [
  {
    icon: Brain,
    title: 'AI Strategy & Roadmap',
    description: 'Develop comprehensive AI adoption strategies aligned with your business objectives and digital transformation goals',
    color: 'from-cyan-500 to-blue-500'
  },
  {
    icon: Zap,
    title: 'Intelligent Automation',
    description: 'Streamline operations with AI-powered automation solutions that reduce costs and increase efficiency',
    color: 'from-emerald-500 to-teal-500'
  },
  {
    icon: TrendingUp,
    title: 'Predictive Analytics',
    description: 'Leverage machine learning models to forecast trends, optimize resources, and make data-driven decisions',
    color: 'from-amber-500 to-orange-500'
  },
  {
    icon: BarChart3,
    title: 'AI-Powered PMO',
    description: 'Transform project management with AI tools for risk prediction, resource optimization, and intelligent scheduling',
    color: 'from-purple-500 to-pink-500'
  }
]

export default function AIServices() {
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false)

  return (
    <>
    <div className="py-24 bg-gradient-to-b from-slate-950 to-slate-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-2 px-4 py-2 bg-cyan-500/10 border border-cyan-500/20 rounded-full mb-4">
            <Sparkles className="w-4 h-4 text-cyan-400" />
            <span className="text-cyan-400 text-sm font-medium">AI-Enhanced Services</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Intelligent Solutions for<br />
            <span className="bg-gradient-to-r from-cyan-400 to-emerald-400 bg-clip-text text-transparent">
              Modern Businesses
            </span>
          </h2>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            Harness the power of artificial intelligence to accelerate growth, optimize operations, and gain competitive advantages
          </p>
        </div>

        {/* AI Services Grid */}
        <div className="grid md:grid-cols-2 gap-8">
          {aiServices.map((service, index) => (
            <div key={index} className="group relative bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-2xl p-8 hover:border-cyan-500/50 transition-all duration-300 hover:shadow-xl hover:shadow-cyan-500/10">
              {/* Gradient Background on Hover */}
              <div className={`absolute inset-0 bg-gradient-to-br ${service.color} opacity-0 group-hover:opacity-5 rounded-2xl transition-opacity duration-300`}></div>
              
              <div className="relative">
                <div className={`inline-flex p-4 bg-gradient-to-br ${service.color} rounded-xl mb-6 group-hover:scale-110 transition-transform duration-300`}>
                  <service.icon className="w-8 h-8 text-white" />
                </div>
                
                <h3 className="text-2xl font-bold text-white mb-4 group-hover:text-cyan-400 transition">
                  {service.title}
                </h3>
                
                <p className="text-gray-400 leading-relaxed">
                  {service.description}
                </p>

                {/* Arrow indicator */}
                <div className="mt-6 flex items-center text-cyan-400 opacity-0 group-hover:opacity-100 transition-opacity">
                  <span className="text-sm font-medium">Learn more</span>
                  <svg className="w-4 h-4 ml-2 group-hover:translate-x-2 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* CTA Banner */}
        <div className="mt-16 bg-gradient-to-r from-cyan-500/10 to-emerald-500/10 border border-cyan-500/20 rounded-2xl p-8 md:p-12 text-center">
          <h3 className="text-3xl font-bold text-white mb-4">
            Ready to Transform with AI?
          </h3>
          <p className="text-gray-300 mb-6 max-w-2xl mx-auto">
            Take our Free AI Assessment to discover your AI readiness, or schedule a consultation to learn how AI can revolutionize your business
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <a
              href="https://ai.triangleprojectconsultants.com"
              target="_blank"
              rel="noopener noreferrer"
              className="group px-8 py-3 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-lg font-medium hover:shadow-xl hover:shadow-amber-500/50 transition-all flex items-center gap-2"
            >
              <Sparkles className="w-5 h-5 group-hover:rotate-12 transition-transform" />
              <span>Take Free AI Assessment</span>
            </a>
            <button 
              onClick={() => setIsBookingModalOpen(true)}
              className="px-8 py-3 bg-gradient-to-r from-cyan-500 to-emerald-500 text-white rounded-lg font-medium hover:shadow-xl hover:shadow-cyan-500/50 transition-all"
            >
              Schedule AI Consultation
            </button>
          </div>
        </div>
      </div>
    </div>

    <BookingModal 
      isOpen={isBookingModalOpen} 
      onClose={() => setIsBookingModalOpen(false)} 
    />
    </>
  )
}

